import React, { useState, useMemo } from 'react'
import {
  Sidebar,
  SidebarHeader,
  SidebarContent,
  //SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarGroupTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarTrigger,
} from 'equal-ds-ui'
import { ArrowLeftCircle, ChevronDown, ChevronRight } from 'lucide-react'

import './sidebar-styles.css'

// Import sidebar-specific configuration
import {
  SIDEBAR_ITEMS,
  SIDEBAR_DEFAULTS
} from './sidebar-config'

// Import types
import type { SidebarItem, SidebarItemGroup } from './sidebar-types'

// Import environment hook
import { useLogoUrl } from '../../hooks/useEnv'

interface FinProSidebarProps {
  className?: string
  headerText?: string
  defaultSelected?: string
  defaultExpanded?: boolean

  /** Dynamic menu groups from sessionStorage. If not provided, uses SIDEBAR_ITEMS */
  menuGroups?: SidebarItemGroup[]

  /** Currently active item ID. If provided, controls which item is highlighted */
  activeItemId?: string | null

  /** Callback when user clicks a menu item. Should trigger Angular navigation */
  onItemClick?: (itemId: string) => void
}

export const FinProSidebar: React.FC<FinProSidebarProps> = ({
  className = '',
  headerText = SIDEBAR_DEFAULTS.headerText,

  // New dynamic props
  menuGroups = undefined,
  activeItemId: _activeItemId = undefined, // Prefix with _ to indicate intentionally unused
  onItemClick: _onItemClick = undefined,   // Handled by SidebarProvider in App.tsx
}) => {
  // State to track which menu items are expanded (for items with subitems)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())

  // Toggle expand/collapse for items with subitems
  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev)
      if (next.has(itemId)) {
        next.delete(itemId)
      } else {
        next.add(itemId)
      }
      return next
    })
  }

  // Get logo URL from environment service
  const logoUrl = useLogoUrl()
  // Helper to create default groups from SIDEBAR_ITEMS (fallback when no menuGroups provided)
  const createDefaultGroups = useMemo((): SidebarItemGroup[] => {
    const createSidebarItems = (items: Array<{ id: string; label: string; icon: any }>) => {
      return items.map(item => ({
        ...item,
        icon: item.icon, // Keep as component, not element
        route: '', // Will be handled by existing routing
        permissionCode: item.id.toUpperCase(),
        visible: true,
      }))
    }

    return [
      {
        id: 'aa',
        label: 'AA ECOSYSTEM',
        items: createSidebarItems(SIDEBAR_ITEMS.aa),
        defaultOpen: true,
        order: 1,
      },
      {
        id: 'bulk',
        label: 'BULK OPERATIONS',
        items: createSidebarItems(SIDEBAR_ITEMS.bulk),
        defaultOpen: true,
        order: 2,
      },
      {
        id: 'analytics',
        label: 'ANALYTICS',
        items: createSidebarItems(SIDEBAR_ITEMS.analytics),
        defaultOpen: true,
        order: 3,
      },
      {
        id: 'admin',
        label: 'ADMIN & SETUP',
        items: createSidebarItems(SIDEBAR_ITEMS.admin),
        defaultOpen: true,
        order: 4,
      },
      {
        id: 'docs',
        label: 'REFERENCE & DOCS',
        items: createSidebarItems(SIDEBAR_ITEMS.docs),
        defaultOpen: true,
        order: 5,
      },
    ]
  }, [])

  // Use menuGroups if provided, otherwise use default groups from config
  const groups = menuGroups || createDefaultGroups

  // Initialize group order from groups
  const initialGroupOrder = useMemo(() => groups.map((g) => g.id), [groups])
  const [groupOrder, setGroupOrder] = useState<string[]>(initialGroupOrder)

  // Update groupOrder when groups change (important for dynamic sidebar data)
  React.useEffect(() => {
    setGroupOrder(groups.map((g) => g.id))
  }, [groups])

  // Initialize item orders for each group
  const [itemOrders, setItemOrders] = useState<Record<string, string[]>>(() => {
    const orders: Record<string, string[]> = {}
    groups.forEach((group) => {
      orders[group.id] = group.items.map((item) => item.id)
    })
    return orders
  })

  // Update itemOrders when groups change (important for dynamic sidebar data)
  React.useEffect(() => {
    const newOrders: Record<string, string[]> = {}
    groups.forEach((group) => {
      newOrders[group.id] = group.items.map((item) => item.id)
    })
    setItemOrders(newOrders)
  }, [groups])

  // Create lookup maps for efficient rendering
  const groupsById = useMemo(() => {
    const map: Record<string, SidebarItemGroup> = {}
    groups.forEach((group) => {
      map[group.id] = group
    })
    return map
  }, [groups])

  const itemsById = useMemo(() => {
    const map: Record<string, Record<string, any>> = {}
    groups.forEach((group) => {
      map[group.id] = {}
      group.items.forEach((item) => {
        map[group.id][item.id] = item
      })
    })
    return map
  }, [groups])

  return (
    <Sidebar aria-label="Primary" className={`react-sidebar-fixed ${className}`}>
      <SidebarHeader className="flex items-center justify-between h-16 px-4">
        <div className="flex items-center gap-3">
          <img
            src={logoUrl}
            onError={(e) => {
              // Fallback to default logo if image fails to load
              (e.target as HTMLImageElement).src = 'assets/images/universal-bank.png';
            }}
            className="w-10 h-10 object-contain flex-shrink-0"
            alt="Organization Logo"
          />
          <span className="font-light text-text-secondary italic">{headerText}</span>
        </div>
        <SidebarTrigger srLabel="Collapse sidebar" className="h-8 w-8 flex-shrink-0">
          <ArrowLeftCircle className="h-5 w-5" />
        </SidebarTrigger>
      </SidebarHeader>
      <SidebarContent reorderableGroups onGroupReorder={(next) => setGroupOrder(next)}>
        {groupOrder.map((groupId) => {
          const group = groupsById[groupId]
          if (!group) return null

          const itemOrder = itemOrders[groupId] || group.items.map((i) => i.id)
          const items = itemsById[groupId] || {}

          return (
            <SidebarGroup key={groupId} groupId={groupId} defaultOpen={group.defaultOpen}>
              <SidebarGroupTrigger>
                <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
              </SidebarGroupTrigger>
              <SidebarGroupContent>
                <SidebarMenu
                  reorderable
                  onReorder={(next) => {
                    setItemOrders((prev) => ({ ...prev, [groupId]: next }))
                  }}
                >
                  {itemOrder.map((itemId) => {
                    const item = items[itemId]
                    if (!item) return null

                    // Create icon element from component
                    const IconComponent = item.icon
                    const iconElement = IconComponent ? <IconComponent className="w-5 h-5" /> : null

                    // Check if item has subitems (nested menu)
                    const hasSubItems = item.subItems && item.subItems.length > 0
                    const isExpanded = expandedItems.has(itemId)

                    if (hasSubItems) {
                      // Render as collapsible parent menu with subitems
                      return (
                        <React.Fragment key={itemId}>
                          <SidebarMenuItem draggable dragId={itemId}>
                            <div className="relative">
                              <SidebarMenuButton
                                itemId={itemId}
                                icon={iconElement}
                                onClick={() => toggleExpand(itemId)}
                              >
                                {item.label}
                              </SidebarMenuButton>
                              <button 
                                onClick={() => toggleExpand(itemId)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 shrink-0 transition-transform duration-200" />
                                )}
                              </button>
                            </div>
                          </SidebarMenuItem>

                          {/* Render subitems when expanded */}
                          {isExpanded && item.subItems?.map((subItem: SidebarItem) => {
                            const SubIconComponent = subItem.icon
                            const subIconElement = SubIconComponent ? <SubIconComponent className="w-4 h-4" /> : null

                            return (
                              <SidebarMenuItem key={subItem.id} className="sidebar-submenu-item">
                                <SidebarMenuButton itemId={subItem.id} icon={subIconElement} className="sidebar-submenu-button">
                                  {subItem.label}
                                </SidebarMenuButton>
                              </SidebarMenuItem>
                            )
                          })}
                        </React.Fragment>
                      )
                    } else {
                      // Render as regular menu item (no subitems)
                      return (
                        <SidebarMenuItem key={itemId} draggable dragId={itemId}>
                          <SidebarMenuButton itemId={itemId} icon={iconElement}>
                            {item.label}
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )
                    }
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          )
        })}
      </SidebarContent>
      {/* <SidebarFooter /> */}
    </Sidebar>
  )
}
